import React, {ChangeEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {ITableDividerHeader, ITableSelectionListItem} from "./types";
import {Checkbox, Chip, FormControlLabel, MenuItem, Tooltip, Typography, withStyles} from "@material-ui/core";
import classNames from "classnames";
import style from "./style.module.scss";
import {renderIf} from "external-services/react-utils-service/index";
import {getOptionLabel, getSectionName} from "./helpers";
import OnlineIcon from "@material-ui/icons/Public";
import WalkinIcon from "@material-ui/icons/DirectionsWalk";
import OnlySharedTablesIcon from "@material-ui/icons/PeopleAlt";
import {ITableItem, JOIN_PREFIX} from "shared-types/index";
import LinkIcon from "@material-ui/icons/Link";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import NotInterestedIcon from "@material-ui/icons/NotInterested";
import MuiTypography from "@material-ui/core/Typography";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";

const isInvalid = (table: ITableSelectionListItem) => !table.free // red / error
const isFree = (table: ITableSelectionListItem) => (table.free && !table.valid); // orange / warning
const isValid = (table: ITableSelectionListItem) => table.valid && table.free; // green / success

const NS = 'TableSeater -> subcomponents';

export function DividerMenuItem({sectionsLookup, table}: {
  table: ITableDividerHeader, sectionsLookup: any
}) {
  return (
    <MenuItem className={style.sectionHeaderWrap}
              data-divider disabled={true} >
      <p className={style.sectionHeader}>
        {getSectionName(sectionsLookup, table)}
      </p>
    </MenuItem>
  )
}

export function CustomMenuItem({
    table, classes, sectionsLookup, manualJoinTables, isSelected,

  }: {
  table: ITableDividerHeader,
  classes: any,
  sectionsLookup: any,
  manualJoinTables: boolean,
  isSelected: boolean,
}) {

  return (
    <MenuItem
      disableRipple
      value={table._id}
      className={classNames({
        [classes.menuItem]: true,
        [classes.isInvalidText]: isInvalid(table),
        [classes.isFreeText]: isFree(table),
        [classes.isValidText]: isValid(table),
        [classes.menuItemSelected]: isSelected
      })}
    >
      <div className={style.menuItemMid}>

        {manualJoinTables ? (
          <FormControlLabel
            className={classes.checkboxMenuItemFC}
            label={
              <Typography dangerouslySetInnerHTML={{__html: getOptionLabel(sectionsLookup, table)}} />
            }
            control={

              /**
               * The isSelected property could not be used to update the MUI icons here because it gets cached by ReactSelect
               * internally (not sure why). However, because the CSS classes still get updated externally for the menu item,
               * css could be used to hide/show different icons.
               */
              <div>
                <CheckBoxOutlineBlankIcon color='primary' className={classes.menuCheckbox} fontSize="large"  />
                <CheckBoxIcon color='primary' className={classes.menuCheckbox} fontSize="large"  />
              </div>
            } />
        ) : (
          <div className={style.menuItemText} dangerouslySetInnerHTML={{__html: getOptionLabel(sectionsLookup, table)}} />
        )}

      </div>
      <div className={style.menuItemIconWrap}>
        {renderIf(table.online, () =>
          <Tooltip title="Online" aria-label="Online" arrow>
            <OnlineIcon className={style.onlineIcon} />
          </Tooltip>
        )}
        {renderIf(table.walkinOnly, () =>
          <Tooltip title="Walk-in only" aria-label="Walk-in only" arrow>
            <WalkinIcon className={style.walkinIcon} />
          </Tooltip>
        )}
        {renderIf(table.shared, () =>
          <Tooltip title="Shared" aria-label="Shared" arrow>
            <OnlySharedTablesIcon />
          </Tooltip>
        )}
        {renderIf(table.valid, () => (
          <>
            {table._id.indexOf(JOIN_PREFIX) === 0 && (
              <Tooltip title="Table join" aria-label="Table join" arrow>
                <LinkIcon className={style.tableJoinIcon} data-testid="link-icon"/>
              </Tooltip>
            )}
            <Tooltip title="Valid" aria-label="Valid" arrow>
              <CheckCircleOutlineIcon data-testid="tick-icon" />
            </Tooltip>
          </>
        ), () => (
          renderIf(table.free, () => (
            table.problemReasons?.length> 0 ?
            <Tooltip title={<span dangerouslySetInnerHTML={{__html: table.problemReasons.join('<br/>')}}></span>} aria-label="Problem" arrow>
              <ErrorOutlineIcon />
            </Tooltip> : <ErrorOutlineIcon />
          ), () => (
            <Tooltip title="Invalid" aria-label="Invalid" arrow>
              <NotInterestedIcon />
            </Tooltip>
          ))
        ))}
      </div>
    </MenuItem>
  );
}

export function CustomChip({table, classes, sectionsLookup, isTypeRecurring = false}: {
  table: ITableDividerHeader, classes: any, sectionsLookup: any, isTypeRecurring?: boolean
}) {
  return (
    <Chip
      label={(
        <span dangerouslySetInnerHTML={{__html: getOptionLabel(sectionsLookup, table, true)}}/>
      )}
      variant="outlined"
      className={classNames({
        [classes.chip]: true,
        [style.fullWidth]: isTypeRecurring,
        [classes.isInvalidChip]: isInvalid(table),
        [classes.isFreeChip]: isFree(table),
        [classes.isValidChip]: isValid(table)
      })}
    />
  )
}

export function getLabelText() {
  return withStyles({
    caption: {
      color: 'rgba(0,0,0,0.54)',
      lineHeight: 1,
      marginBottom: 0,
      display: 'block',
      fontSize: "1.2rem"
    }
  })(MuiTypography);
}
